import * as React from 'react';

import { Avatar, cn, Tooltip } from '@rematter/ui-web';
import { useLocation } from 'wouter';

import { type PresenceMember } from '~/modules/settings/_graphql/subscriptionPresenceEvents';
import { useTranslations } from '~/shared/i18n/useTranslations';
import { usePresence } from '~/shared/providers/presence';
import { getSessionColorName } from '~/shared/providers/presence/sessionColors';

const MAX_VISIBLE = 6;

const getMemberName = (member: PresenceMember) => {
  if (member.name) return member.name;
  return member.id.slice(0, 8);
};

type PresenceCircleProps = {
  member: PresenceMember;
  tip: string;
};

const PresenceCircle = ({ member, tip }: PresenceCircleProps) => {
  const name = getMemberName(member);

  return (
    <Tooltip tip={tip} className="@PresenceCircle">
      <Avatar name={name} size="md" color={getSessionColorName(member.id)} />
    </Tooltip>
  );
};

//
// ⚛️ ⚛️ ⚛️ PAGE PRESENCE ⚛️ ⚛️ ⚛️
//
export const PagePresence = () => {
  const t = useTranslations();
  const [location] = useLocation();
  const { members, isSelf } = usePresence(location);

  const sorted = React.useMemo(() => {
    const all = Array.from(members.values());
    const self: PresenceMember[] = [];
    const others: PresenceMember[] = [];
    for (const m of all) {
      if (isSelf(m.id)) {
        self.push(m);
      } else {
        others.push(m);
      }
    }
    return [...self, ...others];
  }, [members, isSelf]);

  if (sorted.length === 0) return null;

  const visible = sorted.slice(0, MAX_VISIBLE);
  const overflow = sorted.length - MAX_VISIBLE;
  const overflowNames = sorted.slice(MAX_VISIBLE).map(getMemberName).join(', ');

  return (
    <div
      className={cn(
        '@PagePresence group flex items-center',
        '-space-x-5 hover:space-x-0.5',
        'transition-all duration-200 ease-out',
      )}
    >
      {visible.map((member, i) => {
        const self = isSelf(member.id);
        const name = getMemberName(member);
        const tip = self ? t.presence.you(name) : name;

        return (
          <div
            key={member.id}
            className="transition-all duration-200 ease-out"
            style={{ zIndex: visible.length - i }}
          >
            <PresenceCircle member={member} tip={tip} />
          </div>
        );
      })}
      {overflow > 0 && (
        <div className="transition-all duration-200 ease-out">
          <Tooltip tip={overflowNames}>
            <div
              className={cn(
                'flex size-7 shrink-0 items-center justify-center rounded-full border border-foreground/20 bg-foreground/10',
                'text-md font-medium text-foreground',
              )}
            >
              +{overflow}
            </div>
          </Tooltip>
        </div>
      )}
    </div>
  );
};
